import { Dropdown, IDropdownOption, TextField, Checkbox, Text } from '@fluentui/react';
import * as React from 'react';
import { FormEvent, useContext, useEffect,useState } from 'react';
import styled from 'styled-components';

import * as Constants from '../../../../../Common/CommonModels/Constants';
import * as ToolTips from '../../../../../Common/Components/InformationToolTips';
import {
  DisplayRequiredLabel,
  DisplayNonRequiredLabel,
  onRenderLabelEgressOptions,
  onRenderLabelSchema,
} from '../../../../../Common/Components/Labels';
import { CDMRecord, IngestionMethod, ingestionMethodDescriptions } from '../../../../../Common/DataOwnerModels/CDMRecord';
import { DataOwnerRequestContext } from '../../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { validateSchema, checkRequiredInProperties, checkMissingType, checkTypeStructure } from '../../../../../Common/Helper/dataOwnerCdmHelper';
import { goldDomainError, useGetGoldDomains } from '../../../../../Hooks/DataOwner/useGetGoldDomains';
import Ajv from 'ajv';

type EditCdmFormProps = {
  cdm: CDMRecord;
};

export const ingestionTypeOptions: IDropdownOption[] = [
  { key: Constants.CDM.IngestionType_Standard, text: 'Standard' },
  { key: Constants.CDM.IngestionType_Bulk, text: 'Bulk' },
];

export const ingestionMethodOptions: IDropdownOption[] = [
  { key: IngestionMethod.Pull, text: 'Pull', title: ingestionMethodDescriptions[IngestionMethod.Pull] },
  { key: IngestionMethod.Push, text: 'Push', title: ingestionMethodDescriptions[IngestionMethod.Push] },
  { key: IngestionMethod.Shortcut, text: 'Shortcut', title: ingestionMethodDescriptions[IngestionMethod.Shortcut] },
];

export function EditCdmForm(props: EditCdmFormProps): React.ReactElement {
  const { dataOwnerRequestsList, dataOwnerRequestDetails } = useContext(DataOwnerRequestContext);
  const { cdm } = props;

  const cdmIndex = dataOwnerRequestDetails.cdms.findIndex((cdmInList: CDMRecord) => {
    return cdm.id === cdmInList.id;
  });

  const [isJsonArrayType, setIsJsonArrayType] = useState<boolean>(false);
  const [goldDomains, isGoldDomainsLoading, goldDomainsError] = useGetGoldDomains(
    dataOwnerRequestDetails.request?.environment
  );

  // Edit Cdm Errors
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [schemaError, setSchemaError] = useState<boolean>(false);
  const [schemaErrorText, setSchemaErrorText] = useState<string>('');
  const [egressionOptionsError, setEgressionOptionsError] = useState<boolean>(false);
  const [schemaText, setSchemaText] = useState(cdm.schema || "");
  const [cdmNameError, setCdmNameError] = useState<string>('');
  const [schemaAttributes, setSchemaAttributes] = useState<string[]>([]);
  const [schemaAttributeOptions, setSchemaAttributeOptions] = useState<IDropdownOption[]>([]);
  const [primaryKey, setPrimaryKey] = useState<string | undefined>(cdm?.primaryKey || undefined);
  const [alternateKeys, setAlternateKeys] = useState<string[]>(cdm?.alternateKeys || []);
  const [compositeKeys, setCompositeKeys] = useState<string[]>(cdm?.compositeKey || []);

  const cdmTypeOptions: IDropdownOption[] = [
    { key: Constants.CDM.cdmType1OptionKey, text: 'Full Journal' },
    { key: Constants.CDM.cdmType2OptionKey, text: 'Current-Dated Data' },
  ];

  const egressionOptionsDropdownOptions: IDropdownOption[] = [
    { key: 1, text: 'API' },
    { key: 2, text: 'Service Bus' },
    { key: 3, text: 'DataLake' },
  ];

  const ingestionFileTypeOptions: IDropdownOption[] = [
    { key: 'Parquet', text: 'Parquet' },
    { key: 'Delta', text: 'Delta' },
    { key: 'JSON', text: 'JSON' },
    { key: 'AVRO', text: 'AVRO' },
    { key: 'CSV', text: 'CSV' },
  ];

  const ingestionHandshakeTypeOptions: IDropdownOption[] = [
    { key: 'None', text: 'None' },
    { key: Constants.CDM.HandshakeType_Default, text: 'Default' },
    { key: 'Custom', text: 'Custom' },
  ];

  const ingressFrequencyOptions: IDropdownOption[] = [
    { key: '1h', text: 'Every hour' },
    { key: '2h', text: 'Every two hours' },
    { key: '4h', text: 'Every four hours' },
    { key: '6h', text: 'Every six hours' },
    { key: '12h', text: 'Every twelve hours' },
    { key: '1d', text: 'Every day' },
    { key: '2d', text: 'Every alternative day' },
    { key: '7d', text: 'Every week on monday ' },
    { key: '30d', text: 'Every month on 1st' },
  ];

  // UseEffect to check if Schema is JsonArray
  useEffect(() => {
    if (!cdm?.schema || cdm?.schema?.trim()?.length <= 0) {
      setIsJsonArrayType(false);
      return;
    }

    validateSchema(
      cdm, 
      cdmIndex, 
      cdm.schema, 
      dataOwnerRequestDetails, 
      setSchemaError, 
      setSchemaErrorText, 
      setIsJsonArrayType
    );
    try {
      const parsed = JSON.parse(cdm.schema);
      rebuildKeyOptionsFromSchema(parsed);
    } catch (error) {
      console.error("Failed to parse schema:", error);
    }
  }, [cdm.schema]);

  const onChangeDescription = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      description: newValue,
    });
  };

  const onChangePrimaryKey = (_: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    const pk = item ? String(item.key) : undefined;
    setPrimaryKey(pk);

    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
    ...cdm,
    primaryKey: pk || null,
    alternateKeys,
    compositeKey: compositeKeys
    });
  };

  const onChangeAlternateKeys = (_: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (!item) return;
    const key = String(item.key);

    const next = item.selected
      ? Array.from(new Set([...alternateKeys, key]))
      : alternateKeys.filter(k => k !== key);

    setAlternateKeys(next);

    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      primaryKey: primaryKey || null,
      alternateKeys: next,
      compositeKey: compositeKeys
    });
  };

  const onChangeCompositeKeys = (_: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (!item) return;

    const key = String(item.key);

    const next = item.selected
      ? Array.from(new Set([...compositeKeys, key]))
      : compositeKeys.filter(k => k !== key);

    setCompositeKeys(next);

    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      primaryKey: primaryKey || null,
      alternateKeys,
      compositeKey: next
    });
  };

  const ajv = new Ajv();

  const tryExtractPropertiesObject = (parsedSchema: any): Record<string, any> | null => {
    if (parsedSchema?.properties && typeof parsedSchema.properties === 'object') {
      return parsedSchema.properties;
    }
    if (parsedSchema?.items?.properties && typeof parsedSchema.items.properties === 'object') {
      return parsedSchema.items.properties;
    }
    return null;
  };

  const rebuildKeyOptionsFromSchema = (parsedSchema: any) => {
    const propsObj = tryExtractPropertiesObject(parsedSchema);

    const attrs = propsObj ? Object.keys(propsObj) : [];
    setSchemaAttributes(attrs);
    setSchemaAttributeOptions(attrs.map(a => ({ key: a, text: a })));

    const nextPrimaryKey = primaryKey && attrs.includes(primaryKey) ? primaryKey : undefined;

    const filteredAlternate = alternateKeys.filter(k => attrs.includes(k));

    const filteredComposite = compositeKeys.filter(k => attrs.includes(k));

    setPrimaryKey(nextPrimaryKey);
    setAlternateKeys(filteredAlternate);
    setCompositeKeys(filteredComposite);

    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      primaryKey: nextPrimaryKey || null,
      alternateKeys: filteredAlternate,
      compositeKey: filteredComposite
    });
  };
  
  const onChangeSchema = (_: any, newValue: string | undefined) => {
    const schemaStr = newValue || "";
    setSchemaText(schemaStr);
    let errorText = '';
    let nameErrorText = ''
    let updatedName = cdm.name;
    let parsedSchema: any = null;
    let schemaParsed = false;
    console.log("initial name",updatedName )
    try {
      parsedSchema = JSON.parse(schemaStr);
      schemaParsed = true;
      const rawType = parsedSchema?.type;
      const rawTypeList = Array.isArray(rawType) ? rawType : [rawType];
      setIsJsonArrayType(rawTypeList.includes('array'));
      //Validate schema structure
      const isValidSchema = ajv.validateSchema(parsedSchema);
      if (!isValidSchema) {
        const errors = ajv.errors?.[0];
        errorText = Constants.ERROR_MESSAGES.INVALID_JSON_SCHEMA(errors?.message);
        return;
      }
      // Custom recursive check for required fields
      const errors = checkRequiredInProperties(parsedSchema);
      if (errors.length > 0) {
        errorText = errors.join(' | ');
        return;
      }
      // Custom check for missing type
      const typePresenceErrors = checkMissingType(parsedSchema);
      if (typePresenceErrors.length > 0) {
        errorText = typePresenceErrors.join(' | ');
        return;
      }
      // Custom check for type structure
      const structureErrors = checkTypeStructure(parsedSchema);
      if (structureErrors.length > 0) {
        errorText = structureErrors.join(' | ');
        return;
      }
      //Update isJsonArrayType based on schema type
      rebuildKeyOptionsFromSchema(parsedSchema);
      //Update CDM name from $id or items.$id
      if (parsedSchema.$id) {
        if (/\s/.test(parsedSchema.$id)) {
          errorText = Constants.ERROR_MESSAGES.INVALID_ID_WITH_SPACES(""); // or just use 'root' if path is not available
          return;
        }

        updatedName = parsedSchema.$id;
        if (rawTypeList.includes('array') && parsedSchema.items?.$id) {
          updatedName = parsedSchema.items.$id;
        }

        const allNames = dataOwnerRequestDetails.cdms.map((item, index) =>
          index !== cdmIndex ? item.name : null
        ).filter(Boolean);
        
        const prodApprovedAllNames = Array.isArray(dataOwnerRequestDetails?.prodApprovedCdms)
          ? dataOwnerRequestDetails.prodApprovedCdms.map(item => item.name).filter(Boolean)
          : [];

        const nonProdApprovedAllNames = Array.isArray(dataOwnerRequestDetails?.nonProdApprovedCdms)
          ? dataOwnerRequestDetails.nonProdApprovedCdms.map(item => item.name).filter(Boolean)
          : [];

        // TODO: The comparison for duplicates cannot just be simple name in existing lists... it needs to consider that the update is to an existing CDM and not fail for duplicate name in that condition
        // if (allNames.includes(updatedName) || nonProdApprovedAllNames.includes(updatedName) || prodApprovedAllNames.includes(updatedName)) {
        //   setCdmNameError(Constants.ERROR_MESSAGES.ENTITY_NAME_ALREADY_EXIST())
        //   nameErrorText = Constants.ERROR_MESSAGES.ENTITY_NAME_ALREADY_EXIST()
        //   return;
        // }else{
        //   setCdmNameError('')
        //   nameErrorText = ''
        // }

      } 

    } catch (err) {
      errorText = Constants.ERROR_MESSAGES.INVALID_JSON(err);
      setIsJsonArrayType(false);
    } finally {
      setSchemaErrorText(errorText);
      let filteredPrimaryKey = primaryKey;
      let filteredAlternateKeys = alternateKeys;
      let filteredCompositeKeys = compositeKeys;

      if (schemaParsed) {
        const propsObj = tryExtractPropertiesObject(parsedSchema);
        const attrs = propsObj ? Object.keys(propsObj) : [];
        filteredPrimaryKey = primaryKey && attrs.includes(primaryKey) ? primaryKey : undefined;
        filteredAlternateKeys = alternateKeys.filter(k => attrs.includes(k));
        filteredCompositeKeys = compositeKeys.filter(k => attrs.includes(k));
      }

      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        schema: schemaStr,
        schemaErrorText: errorText,
        cdmNameError: nameErrorText,
        name: updatedName,
        primaryKey: filteredPrimaryKey || null,
        alternateKeys: filteredAlternateKeys,
        compositeKey: filteredCompositeKeys
      });
    }
  };

  const onChangeCdmType = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    if (item.key == Constants.CDM.cdmType2OptionKey) {
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        type: item.key.toString(),
        expression: 'DateOnly.FromDateTime(Convert.ToDateTime(startDate).ToUniversalTime()) <= DateOnly.FromDateTime(DateTime.UtcNow) && DateOnly.FromDateTime(DateTime.UtcNow) <= DateOnly.FromDateTime(Convert.ToDateTime(endDate).ToUniversalTime())',
        cardinality: 1,
      });
    } else {
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        type: item.key.toString(),
        expression: null,
        cardinality: 0,
      });
    }
  };

  const onChangeIngestionType = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    if (item.key.toString() == Constants.CDM.IngestionType_Standard) {
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        ingestionType: item.key.toString(),
        ingestionOptions: null,
      });
      return;
    }
    // Else Bulk Ingestion type
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionType: item.key.toString(),
      ingestionOptions: { ingestionMethod: IngestionMethod.Pull },
    });
  };

  const onChangeIngestionMethod = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (!item) return;
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, ingestionMethod: item.key.toString() },
    });
  };

  const onChangeIngestionBasePath = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, basePath: newValue },
    });
  };
  const onChangeIngestionFileType = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, fileType: item.key.toString() },
    });
  };

  const onChangeIngestionFileName = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, fileName: newValue },
    });
  };

  const onChangeIngestionFolderPath = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, folderPath: newValue },
    });
  };

  const onChangeIngestionHandshakeType = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    if (item.key.toString() != Constants.CDM.HandshakeType_Default) {
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        ingestionOptions: {
          ...cdm.ingestionOptions,
          handshakeType: item.key.toString(),
          defaultHandshakeFilePath: null,
        },
      });
      return;
    }
    // Else Default Handshake Type
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, handshakeType: item.key.toString() },
    });
  };

  const onChangeIngestionDefaultHandshakeFilePath = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, defaultHandshakeFilePath: newValue },
    });
  };

  const onChangeIngestionHandshakeDescription = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, handshakeDescription: newValue },
    });
  };

  const onChangeIngestionIdProperty = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, idProperty: newValue },
    });
  };

  const onChangeIngressFrequency = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      ingestionOptions: { ...cdm.ingestionOptions, ingressFrequency: item.key.toString() },
    });
  };

  const onChangeEgressionOptions = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item && item.selected) {
      setEgressionOptionsError(false);
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        egressOptions: [...cdm.egressOptions, item.key as number],
      });
    }
    if (item && !item.selected) {
      const newEgressionOptions = cdm.egressOptions.filter((key) => key !== item.key);
      if (newEgressionOptions.length === 0) {
        setEgressionOptionsError(true);
      }
      dataOwnerRequestDetails.updateCdmList(cdmIndex, {
        ...cdm,
        egressOptions: newEgressionOptions,
      });
    }
  };

  const onChangeEO14117Protected = (event?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void => {
    dataOwnerRequestDetails.updateCdmList(cdmIndex, {
      ...cdm,
      isEO14117Protected: checked || false,
    });
  };

  const getDescriptionErrorMessage = (newValue?: string) => {
    if (!newValue) {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDescriptionError(false);
      return '';
    } else {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    }
  };

  const handleFocusOutEgressionOptions = () => {
    if (cdm.egressOptions.length == 0) {
      setEgressionOptionsError(true);
    } else {
      setEgressionOptionsError(false);
    }
  };

  return (
    <>
      <EditCdmContainer>
        <TextField
          data-testid="edit-cdm-form-name"
          label="CDM name"
          id="editCdmFormName"
          placeholder="Enter custom name for your reference"
          value={cdm.name}
          disabled={true}
          errorMessage={cdmNameError}
        />
        <TextField
          data-testid="edit-cdm-form-description"
          label="Description"
          id="description"
          placeholder="Enter custom description"
          defaultValue={cdm.description}
          onChange={onChangeDescription}
          onGetErrorMessage={getDescriptionErrorMessage}
          required={false}
          validateOnLoad={false}
          validateOnFocusOut={true}
          multiline={true}
          autoAdjustHeight={true}
        />
        <TextField
          data-testid="edit-cdm-form-schema"
          onRenderLabel={onRenderLabelSchema}
          id="schema"
          placeholder="Enter Schema in JSON format"
          defaultValue={cdm.schema}
          onChange={onChangeSchema}
          errorMessage={schemaErrorText}
          validateOnLoad={false}
          validateOnFocusOut={true}
          multiline={true}
          autoAdjustHeight={true}
        />
        <Dropdown
          id="primaryKey"
          placeholder="Select Primary key attribute"
          ariaLabel="Primary Key"
          label="Primary key"
          selectedKey={primaryKey}
          options={schemaAttributeOptions}
          onChange={onChangePrimaryKey}
          multiSelect={false}
          disabled={schemaAttributeOptions.length === 0 || !!schemaErrorText}
        />
        <Dropdown
          id="alternateKeys"
          placeholder="Select Alternate key attributes"
          ariaLabel="Alternate Keys"
          label="Alternate keys"
          selectedKeys={alternateKeys}
          options={schemaAttributeOptions}
          onChange={onChangeAlternateKeys}
          multiSelect={true}
          disabled={schemaAttributeOptions.length === 0 || !!schemaErrorText}
        />
        <Dropdown
          id="compositeKeys"
          placeholder="Select Composite key attributes"
          ariaLabel="Composite Keys"
          label="Composite keys"
          selectedKeys={compositeKeys}
          options={schemaAttributeOptions}
          onChange={onChangeCompositeKeys}
          multiSelect={true}
          disabled={schemaAttributeOptions.length === 0 || !!schemaErrorText}
        />
        {isJsonArrayType && (
          <Dropdown
            label="CDM Type"
            id="cdmType"
            placeholder="Select the Entity type"
            ariaLabel="CDM Type"
            selectedKey={cdm?.type}
            options={cdmTypeOptions}
            onChange={onChangeCdmType}
            multiSelect={false}
            required={true}
          />
        )}
        <Dropdown
          data-testid="edit-cdm-form-egression-options"
          onRenderLabel={onRenderLabelEgressOptions}
          id="egressionOptions"
          placeholder="Select Egression"
          ariaLabel="Egression Options"
          selectedKeys={cdm.egressOptions}
          onChange={onChangeEgressionOptions}
          onBlur={handleFocusOutEgressionOptions}
          errorMessage={egressionOptionsError ? 'Please select an Egression Option' : ''}
          options={egressionOptionsDropdownOptions}
          multiSelect={true}
        />
        <Dropdown
          onRenderLabel={() => DisplayRequiredLabel('Ingestion Type', ToolTips.ingestionTypeTooltipProps)}
          id="ingestionType"
          data-testid="edit-cdm-form-ingestion-type"
          placeholder="Select the Ingestion Type"
          ariaLabel="IngestionType"
          selectedKey={cdm?.ingestionType}
          options={ingestionTypeOptions}
          onChange={onChangeIngestionType}
          multiSelect={false}
          required={true}
        />
        <Checkbox
          label="EO14117 Protected"
          checked={cdm?.isEO14117Protected || false}
          onChange={onChangeEO14117Protected}
          data-testid="edit-cdm-form-eo14117-protected"
          onRenderLabel={() => DisplayNonRequiredLabel('EO14117 Protected', ToolTips.eo14117ProtectedTooltipProps)}
        />
        <Dropdown
          label="Domain"
          ariaLabel="Domain"
          placeholder="Select the Gold domain"
          selectedKey={cdm.domain || null}
          options={goldDomains.map((domain) => ({ key: domain, text: domain }))}
          onChange={(event, option) => option && dataOwnerRequestDetails.updateCdmList(cdmIndex, {
            ...cdm,
            domain: String(option.key),
          })}
          errorMessage={goldDomainError(cdm.domain, goldDomains, isGoldDomainsLoading, goldDomainsError)}
          disabled={isGoldDomainsLoading}
          required
        />
        <Dropdown
          label="Target schema"
          ariaLabel="Target schema"
          placeholder="Select the publication schema"
          selectedKey={cdm.targetSchema}
          options={[
            { key: 'dbo', text: 'dbo' },
            { key: 'analytical', text: 'analytical' },
            { key: 'snapshot', text: 'snapshot' },
          ]}
          onChange={(event, option) => option && dataOwnerRequestDetails.updateCdmList(cdmIndex, {
            ...cdm,
            targetSchema: option.key as CDMRecord['targetSchema'],
          })}
          errorMessage={!cdm.targetSchema ? 'Select an explicit target schema before submitting.' : ''}
          required
        />
        {cdm?.ingestionType == Constants.CDM.IngestionType_Bulk && (
          <>
            <Dropdown
              onRenderLabel={() => DisplayRequiredLabel('Ingestion Method', ToolTips.ingestionMethodTooltipProps)}
              id="ingestionMethod"
              data-testid="edit-cdm-form-ingestion-method"
              placeholder="Select the Ingestion Method"
              ariaLabel="IngestionMethod"
              selectedKey={cdm?.ingestionOptions?.ingestionMethod ?? null}
              options={ingestionMethodOptions}
              onChange={onChangeIngestionMethod}
              multiSelect={false}
              required={true}
              errorMessage={!cdm?.ingestionOptions?.ingestionMethod ? 'Select an ingestion method.' : ''}
            />
            {cdm?.ingestionOptions?.ingestionMethod && (
              <Text variant="small" data-testid="edit-cdm-form-ingestion-method-description">
                {ingestionMethodDescriptions[cdm.ingestionOptions.ingestionMethod]}
              </Text>
            )}
            <TextField
              onRenderLabel={() =>
                DisplayRequiredLabel('Storage Base Path', ToolTips.ingestionStorageBasePathTooltipProps)
              }
              id="basePath"
              placeholder="Enter Storage Base Path for Bulk Ingestion"
              defaultValue={cdm?.ingestionOptions?.basePath}
              onChange={onChangeIngestionBasePath}
            />
            <Dropdown
              onRenderLabel={() => DisplayRequiredLabel('File Type', ToolTips.ingestionFileTypeTooltipProps)}
              id="fileType"
              placeholder="Select the File Type"
              ariaLabel="FileType"
              selectedKey={cdm?.ingestionOptions?.fileType}
              options={ingestionFileTypeOptions}
              onChange={onChangeIngestionFileType}
              multiSelect={false}
              required={true}
            />
            <TextField
              onRenderLabel={() => DisplayRequiredLabel('File Name', ToolTips.ingestionFileNameTooltipProps)}
              id="fileName"
              data-testid="edit-cdm-form-ingestion-options-filename"
              placeholder="Enter File Name for Bulk Data Ingestion"
              defaultValue={cdm?.ingestionOptions?.fileName}
              onChange={onChangeIngestionFileName}
            />
            <TextField
              onRenderLabel={() => DisplayRequiredLabel('Folder Path', ToolTips.ingestionFolderPathTooltipProps)}
              id="folderPath"
              placeholder="Enter Folder Path for Bulk Data Ingestion"
              defaultValue={cdm?.ingestionOptions?.folderPath}
              onChange={onChangeIngestionFolderPath}
            />
            <TextField
              onRenderLabel={() => DisplayRequiredLabel('ID Property', ToolTips.ingestionIdPropertyTooltipProps)}
              id="idProperty"
              placeholder="Enter Id Property"
              defaultValue={cdm?.ingestionOptions?.idProperty}
              onChange={onChangeIngestionIdProperty}
            />
            <Dropdown
              onRenderLabel={() => DisplayRequiredLabel('Handshake Type', ToolTips.ingestionHandshakeTypeTooltipProps)}
              id="handshakeType"
              placeholder="Select the Handshake Type"
              ariaLabel="HandshakeType"
              selectedKey={cdm?.ingestionOptions?.handshakeType}
              options={ingestionHandshakeTypeOptions}
              onChange={onChangeIngestionHandshakeType}
              multiSelect={false}
              required={true}
            />
            {cdm?.ingestionOptions?.handshakeType == Constants.CDM.HandshakeType_Default && (
              <TextField
                onRenderLabel={() =>
                  DisplayRequiredLabel('Handshake File Path', ToolTips.ingestionHandshakeFilePathTooltipProps)
                }
                id="defaultHandshakeFilePath"
                placeholder="Enter File Path to Handshake File"
                defaultValue={cdm?.ingestionOptions?.defaultHandshakeFilePath}
                onChange={onChangeIngestionDefaultHandshakeFilePath}
              />
            )}
            <TextField
              label="Handshake Description"
              id="handshakeDescription"
              placeholder="Enter custom handshake description"
              defaultValue={cdm?.ingestionOptions?.defaultHandshakeFilePath}
              onChange={onChangeIngestionHandshakeDescription}
              multiline={true}
              autoAdjustHeight={true}
            />
            <Dropdown
              onRenderLabel={() => DisplayRequiredLabel('Ingress Frequency', ToolTips.ingressFrequencyTooltipProps)}
              id="ingressFrequency"
              placeholder="Select the Ingress Frequency"
              ariaLabel="ingressFrequency"
              selectedKey={cdm?.ingestionOptions?.ingressFrequency}
              options={ingressFrequencyOptions}
              onChange={onChangeIngressFrequency}
              multiSelect={false}
              required={true}
            />
          </>
        )}
      </EditCdmContainer>
    </>
  );
}

const EditCdmContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  padding-top: 10px;
  padding-bottom: 30px;
`;
